/**
 * What the submission panel captures automatically (#1170): where the person
 * was when they opened it. Sent as the ticket's `context` (#1168) unless they
 * untick "anhängen".
 *
 * Everything here is coarse on purpose — browser and OS without build
 * numbers, the window title the person can see anyway. It is display only for
 * the team and the reporter, so the chips show exactly what is sent.
 */
import type { TicketContext } from '@/services/bug-report-service'

/** The BFF's per-field caps (`TicketContext` in `backend/app/schemas/bug_reports.py`);
 *  a longer value is a 422 there, so it is cut here. */
export const CONTEXT_FIELD_LIMITS = {
  system: 50,
  app: 100,
  window: 200,
  route: 500,
  app_version: 50,
  browser: 300,
  os: 100,
  client_name: 200,
} as const satisfies Partial<Record<keyof TicketContext, number>>

/** Where the panel was opened: what the host (desktop or standalone shell) knows. */
export interface TicketContextSource {
  system: string
  app?: string | null
  window?: string | null
  route?: string | null
}

export interface BrowserInfo {
  browser: string | null
  os: string | null
}

/** Browser name + major version and the OS family, from the user agent. */
export function parseUserAgent(userAgent: string): BrowserInfo {
  return { browser: browserOf(userAgent), os: osOf(userAgent) }
}

function browserOf(ua: string): string | null {
  // Order matters: Edge and Opera carry "Chrome/", Chrome carries "Safari/".
  const rules: Array<[RegExp, string]> = [
    [/Edg(?:e|A|iOS)?\/(\d+)/, 'Edge'],
    [/OPR\/(\d+)/, 'Opera'],
    [/Firefox\/(\d+)/, 'Firefox'],
    [/FxiOS\/(\d+)/, 'Firefox'],
    [/CriOS\/(\d+)/, 'Chrome'],
    [/Chrome\/(\d+)/, 'Chrome'],
    [/Version\/(\d+)(?:\.\d+)*.*Safari\//, 'Safari'],
  ]
  for (const [pattern, name] of rules) {
    const match = ua.match(pattern)
    if (match) return `${name} ${match[1]}`
  }
  return null
}

function osOf(ua: string): string | null {
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS'
  if (/Android/.test(ua)) return 'Android'
  if (/CrOS/.test(ua)) return 'ChromeOS'
  if (/Windows/.test(ua)) return 'Windows'
  if (/Mac OS X|Macintosh/.test(ua)) return 'macOS'
  if (/Linux/.test(ua)) return 'Linux'
  return null
}

function capped(value: string | null | undefined, max: number): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed
}

export interface BuildTicketContextInput {
  source: TicketContextSource
  appVersion: string
  userAgent: string
  clientName?: string | null
  now?: Date
}

/** The context as the BFF accepts it: allow-listed keys, capped, empty ones left out. */
export function buildTicketContext({ source, appVersion, userAgent, clientName, now = new Date() }: BuildTicketContextInput): TicketContext {
  const { browser, os } = parseUserAgent(userAgent)
  const context: TicketContext = {
    system: capped(source.system, CONTEXT_FIELD_LIMITS.system),
    app: capped(source.app, CONTEXT_FIELD_LIMITS.app),
    window: capped(source.window, CONTEXT_FIELD_LIMITS.window),
    route: capped(source.route, CONTEXT_FIELD_LIMITS.route),
    app_version: capped(appVersion, CONTEXT_FIELD_LIMITS.app_version),
    browser: capped(browser, CONTEXT_FIELD_LIMITS.browser),
    os: capped(os, CONTEXT_FIELD_LIMITS.os),
    client_name: capped(clientName, CONTEXT_FIELD_LIMITS.client_name),
    captured_at: now.toISOString(),
  }
  return Object.fromEntries(Object.entries(context).filter(([, value]) => value !== undefined)) as TicketContext
}

export interface ContextChip {
  key: 'system' | 'window' | 'route' | 'app_version' | 'browser' | 'client_name' | 'captured_at'
  label: string
}

/**
 * The chips the panel shows, in reading order. App and window share one chip
 * ("cdbDrive – Lieferscheine"), browser and OS another ("Chrome 141 · macOS").
 */
export function contextChips(
  context: TicketContext,
  format: { version: (version: string) => string; date: (iso: string) => string },
): ContextChip[] {
  const chips: ContextChip[] = []
  if (context.system) chips.push({ key: 'system', label: context.system })
  const where = context.window ?? context.app
  if (where) chips.push({ key: 'window', label: where })
  if (context.route) chips.push({ key: 'route', label: context.route })
  if (context.app_version) chips.push({ key: 'app_version', label: format.version(context.app_version) })
  const device = [context.browser, context.os].filter(Boolean).join(' · ')
  if (device) chips.push({ key: 'browser', label: device })
  if (context.client_name) chips.push({ key: 'client_name', label: context.client_name })
  if (context.captured_at) chips.push({ key: 'captured_at', label: format.date(context.captured_at) })
  return chips
}

/** "cdbDrive – Lieferscheine": the title the window shows, with its subtitle. */
export function windowLabel(title: string | undefined, subtitle: string | undefined): string | null {
  const parts = [title, subtitle].map((part) => part?.trim()).filter(Boolean)
  return parts.length > 0 ? parts.join(' – ') : null
}

/** The desktop's system label: the chat window is its own product, everything else is webOS. */
export function desktopSystemFor(appId: string | null | undefined): string {
  return appId === 'communicator2' ? 'Chat' : 'webOS'
}

/** The standalone shell's system label, from the route (`/chat`, `/drive`, …). */
export function standaloneSystemFor(pathname: string): string {
  return pathname.startsWith('/drive') ? 'Drive' : 'Chat'
}
